<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  session: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const config = ref({
  ignore: {
    all: false,
    status: false,
    groups: false,
    channels: false,
    broadcast: false,
  },
  messageFormat: {
    addAgentName: false,
    textTemplate: '*{{{sender.name}}}*:\n{{{content}}}',
    mediaCaptionTemplate: '*{{{sender.name}}}*:\n{{{content}}}',
  },
});
const isSaving = ref(false);

const ignoreOptions = [
  {
    key: 'all',
    icon: 'i-lucide-ban',
    color: 'text-n-red-11',
  },
  {
    key: 'status',
    icon: 'i-lucide-circle-dot',
    color: 'text-n-blue-11',
  },
  {
    key: 'groups',
    icon: 'i-lucide-users',
    color: 'text-n-green-11',
  },
  {
    key: 'channels',
    icon: 'i-lucide-hash',
    color: 'text-n-purple-11',
  },
  {
    key: 'broadcast',
    icon: 'i-lucide-radio',
    color: 'text-n-orange-11',
  },
];

const defaultTextTemplate = '*{{{sender.name}}}*:\n{{{content}}}';
const defaultMediaCaptionTemplate = '*{{{sender.name}}}*:\n{{{content}}}';

// Variable names for display in the UI (avoid Vue template parsing issues with triple braces)
const varSenderName = '{{{sender.name}}}';
const varContent = '{{{content}}}';

const textTemplatePreview = computed(() => {
  const template =
    config.value.messageFormat.textTemplate || defaultTextTemplate;
  return template
    .replace('{{{sender.name}}}', 'João')
    .replace('{{{content}}}', 'Hello, how can I help you?');
});

const mediaCaptionPreview = computed(() => {
  const template =
    config.value.messageFormat.mediaCaptionTemplate ||
    defaultMediaCaptionTemplate;
  return template
    .replace('{{{sender.name}}}', 'João')
    .replace('{{{content}}}', 'Here is the file you requested');
});

const resetForm = () => {
  const sessionConfig = props.session.config || {};
  config.value = {
    ignore: {
      all: sessionConfig.ignore?.all || false,
      status: sessionConfig.ignore?.status || false,
      groups: sessionConfig.ignore?.groups || false,
      channels: sessionConfig.ignore?.channels || false,
      broadcast: sessionConfig.ignore?.broadcast || false,
    },
    messageFormat: {
      addAgentName: sessionConfig.messageFormat?.addAgentName || false,
      textTemplate:
        sessionConfig.messageFormat?.textTemplate || defaultTextTemplate,
      mediaCaptionTemplate:
        sessionConfig.messageFormat?.mediaCaptionTemplate ||
        defaultMediaCaptionTemplate,
    },
  };
};

const saveConfig = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('wahaSessions/updateConfig', {
      id: props.session.id,
      config: config.value,
    });
    useAlert(t('WAHA_SESSIONS.CONFIG.SUCCESS_MESSAGE'));
    await store.dispatch('wahaSessions/get');
    dialogRef.value.close();
  } catch (error) {
    useAlert(error.message || t('WAHA_SESSIONS.CONFIG.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};

watch(
  () => props.session,
  () => {
    resetForm();
  },
  { immediate: true, deep: true }
);

defineExpose({
  dialogRef,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WAHA_SESSIONS.CONFIG.TITLE', { name: session.name })"
    width="2xl"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-6 max-h-[70vh] overflow-y-auto pr-2">
      <!-- Ignore Settings -->
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('WAHA_SESSIONS.CONFIG.IGNORE.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('WAHA_SESSIONS.CONFIG.IGNORE.DESCRIPTION') }}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-3">
          <label
            v-for="option in ignoreOptions"
            :key="option.key"
            class="flex items-center gap-3 p-4 border border-n-weak rounded-lg cursor-pointer transition-all hover:border-n-blue-7 hover:bg-n-blue-2"
            :class="{
              'opacity-50 pointer-events-none':
                option.key !== 'all' && config.ignore.all,
              'border-n-blue-7 bg-n-blue-2': config.ignore[option.key],
            }"
          >
            <input
              v-model="config.ignore[option.key]"
              type="checkbox"
              class="w-5 h-5 rounded border-n-weak cursor-pointer"
              :disabled="option.key !== 'all' && config.ignore.all"
            />
            <span class="text-xl" :class="[option.icon, option.color]" />
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  t(
                    `WAHA_SESSIONS.CONFIG.IGNORE.${option.key.toUpperCase()}.LABEL`
                  )
                }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  t(
                    `WAHA_SESSIONS.CONFIG.IGNORE.${option.key.toUpperCase()}.DESCRIPTION`
                  )
                }}
              </span>
            </div>
          </label>
        </div>
      </div>

      <!-- Message Format Settings -->
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.DESCRIPTION') }}
          </p>
        </div>

        <div class="flex flex-col gap-4">
          <label
            class="flex items-center justify-between gap-3 p-4 border border-n-weak rounded-lg cursor-pointer transition-all hover:border-n-blue-7"
            :class="{
              'border-n-blue-7 bg-n-blue-2': config.messageFormat.addAgentName,
            }"
          >
            <div class="flex items-center gap-3">
              <span class="text-xl i-lucide-user text-n-blue-11" />
              <div class="flex flex-col gap-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    t(
                      'WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.ADD_AGENT_NAME.LABEL'
                    )
                  }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{
                    t(
                      'WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.ADD_AGENT_NAME.DESCRIPTION'
                    )
                  }}
                </span>
              </div>
            </div>
            <Switch v-model="config.messageFormat.addAgentName" />
          </label>

          <div
            v-if="config.messageFormat.addAgentName"
            class="flex flex-col gap-4 p-4 border border-n-weak rounded-lg bg-n-slate-2"
          >
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  t('WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.TEXT_TEMPLATE.LABEL')
                }}
              </label>
              <textarea
                v-model="config.messageFormat.textTemplate"
                rows="3"
                class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background focus:border-n-blue-7 focus:outline-none font-mono"
                :placeholder="defaultTextTemplate"
              />
              <p class="text-xs text-n-slate-11">
                {{
                  t('WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.TEXT_TEMPLATE.HELP')
                }}
              </p>
              <div class="p-3 border border-n-weak rounded-lg bg-n-slate-3">
                <p class="text-xs text-n-slate-11 mb-1">
                  {{ t('WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.PREVIEW') }}:
                </p>
                <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
                  {{ textTemplatePreview }}
                </p>
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  t(
                    'WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.MEDIA_CAPTION_TEMPLATE.LABEL'
                  )
                }}
              </label>
              <textarea
                v-model="config.messageFormat.mediaCaptionTemplate"
                rows="3"
                class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background focus:border-n-blue-7 focus:outline-none font-mono"
                :placeholder="defaultMediaCaptionTemplate"
              />
              <p class="text-xs text-n-slate-11">
                {{
                  t(
                    'WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.MEDIA_CAPTION_TEMPLATE.HELP'
                  )
                }}
              </p>
              <div class="p-3 border border-n-weak rounded-lg bg-n-slate-3">
                <p class="text-xs text-n-slate-11 mb-1">
                  {{ t('WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.PREVIEW') }}:
                </p>
                <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
                  {{ mediaCaptionPreview }}
                </p>
              </div>
            </div>

            <div class="p-3 border border-n-amber-6 rounded-lg bg-n-amber-2">
              <p class="text-xs text-n-amber-11">
                <span class="font-medium">
                  {{
                    t('WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.VARIABLES.TITLE')
                  }}:
                </span>
                <code class="ml-1 px-1 bg-n-amber-3 rounded">
                  {{ varSenderName }}
                </code>
                -
                {{
                  t(
                    'WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.VARIABLES.SENDER_NAME'
                  )
                }},
                <code class="ml-1 px-1 bg-n-amber-3 rounded">
                  {{ varContent }}
                </code>
                -
                {{ t('WAHA_SESSIONS.CONFIG.MESSAGE_FORMAT.VARIABLES.CONTENT') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="flex gap-2 justify-end pt-4 border-t border-n-weak">
        <Button
          slate
          :label="t('WAHA_SESSIONS.CONFIG.CANCEL')"
          @click="dialogRef.close()"
        />
        <Button
          :label="t('WAHA_SESSIONS.CONFIG.SAVE')"
          :is-loading="isSaving"
          @click="saveConfig"
        />
      </div>
    </div>
  </Dialog>
</template>
